import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Compass, Layers, Users, Target, Play, Star, Telescope, Sparkles, Quote, Flame, Brain, Hammer, HeartHandshake, X } from 'lucide-react';
import AltHeader from '../components/AltHeader';
import AltFooter from '../components/AltFooter';
import GooglePlayIcon from '../components/GooglePlayIcon';

/* Peerlist monogram — only used on the Ava B. review card here, so kept
   local instead of moving into AltFooter. */
const PeerlistIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M4.5 3.75A1.5 1.5 0 0 1 6 2.25h7.25a6.75 6.75 0 0 1 0 13.5H9V20.25a1.5 1.5 0 0 1-3 0V5.25H4.5V3.75zM9 5.25v7.5h4.25a3.75 3.75 0 0 0 0-7.5H9z"/>
  </svg>
);

/* Small brand-mark glyphs for the unified download CTA. Kept inline so the
   button stays a single self-contained element with no extra deps. */
const AppleIcon = (props) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor" {...props}>
    <path d="M16.365 1.43c0 1.14-.43 2.21-1.16 3.01-.79.86-2.06 1.52-3.05 1.43-.13-1.13.41-2.27 1.13-3.01.81-.85 2.21-1.49 3.08-1.43zM20.5 17.27c-.55 1.27-.81 1.83-1.51 2.96-.99 1.59-2.39 3.57-4.12 3.59-1.54.02-1.94-1-4.03-.99-2.09.01-2.53 1.01-4.07.99-1.74-.02-3.06-1.83-4.05-3.42-2.78-4.45-3.07-9.67-1.36-12.45 1.21-1.96 3.13-3.11 4.92-3.11 1.83 0 2.98 1 4.5 1 1.47 0 2.36-1 4.48-1 1.6 0 3.3.87 4.51 2.38-3.97 2.18-3.32 7.85.73 8.05z"/>
  </svg>
);

const WindowsIcon = (props) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor" {...props}>
    <path d="M3 5.557 11 4.4v7.6H3V5.557zM12 4.247 21 3v9h-9V4.247zM3 13h8v7.6L3 19.443V13zm9 0h9v8.997L12 20.747V13z"/>
  </svg>
);

/**
 * Homepage — bsky.social-inspired manifesto layout with sky-gradient
 * parallax, oversized italic typography, the Four Worlds card grid,
 * floating review cards, founder section, mission/vision, and the
 * full inline footer. Replaces the original Home.jsx.
 */
const HomeAlt2 = () => {
  // Teaser video modal — toggled by the "Play teaser" CTA in the
  // manifesto section. Hosts a Vimeo iframe (autoplay on open).
  const [teaserOpen, setTeaserOpen] = useState(false);
  // Background ambient music — we WANT it on by default, but browsers
  // block autoplay-with-sound until the user has interacted with the page.
  // So: render `<audio>` unmuted and try to play; if blocked, attach a
  // one-shot gesture listener that starts playback on the first click /
  // Background ambient music is now owned by `<BackgroundMusic />` at
  // the App shell level so it persists across route changes. The
  // homepage no longer owns the <audio> element or the mute toggle.
  // When the teaser video opens we momentarily pause the app-level
  // audio by dispatching a custom event; BackgroundMusic listens for
  // it. When the teaser closes we restore the previous state.

  useEffect(() => {
    // Title is set by the server-side prerender / FastAPI SEO injection,
    // but if the user navigates client-side (e.g., from /blog -> /) we
    // make sure the tab title matches the homepage meta.
    document.title = 'Levanta – Structured Personal Growth App | Build Real Skills';
  }, []);

  // Animated 0 → 82 counter for the proof-section gauge. Triggers via
  // IntersectionObserver the first time the tile scrolls into view, then
  // disconnects (no re-trigger on subsequent scrolls). Eases out-cubic
  // over ~1.6s, matched to the SVG arc's stroke-dashoffset transition
  // so the number and the arc finish together.
  const gaugeRef = useRef(null);
  const nexaVideoRef = useRef(null);
  // React sets `muted` as an attribute, not the DOM property, so a <video muted>
  // can actually stay UNMUTED at the property level and only appear silent
  // because iOS blocks unmuted autoplay. When another audio session activates
  // (e.g. the NEXA app starting mic recording in another tab), that video's
  // audio can leak out. Force the property muted and keep re-asserting it.
  useEffect(() => {
    const v = nexaVideoRef.current;
    if (!v) return;
    const enforce = () => { v.muted = true; v.defaultMuted = true; v.volume = 0; };
    enforce();
    v.addEventListener('volumechange', enforce);
    v.addEventListener('play', enforce);
    return () => {
      v.removeEventListener('volumechange', enforce);
      v.removeEventListener('play', enforce);
    };
  }, []);
  const [gaugeValue, setGaugeValue] = useState(0);
  const [gaugeFilled, setGaugeFilled] = useState(false);
  useEffect(() => {
    const el = gaugeRef.current;
    if (!el) return;
    const target = 82;
    const duration = 1800;
    let started = false;
    const observer = new IntersectionObserver(
      (entries) => {
        if (started || !entries[0].isIntersecting) return;
        started = true;
        setGaugeFilled(true);
        const t0 = performance.now();
        const tick = (now) => {
          const t = Math.min(1, (now - t0) / duration);
          const eased = 1 - Math.pow(1 - t, 3);
          setGaugeValue(Math.round(target * eased));
          if (t < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
        observer.disconnect();
      },
      { threshold: 0.45 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Esc closes the teaser modal; lock body scroll while it's open so
  // the parallax behind doesn't move under the dialog. Also pause the
  // background ambient music while the video is playing — the music
  // resumes automatically when the modal closes (unless the user has
  // explicitly muted it via the toggle, in which case we leave it
  // paused).
  useEffect(() => {
    if (!teaserOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setTeaserOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Ask BackgroundMusic (App shell) to pause while teaser is playing
    window.dispatchEvent(new CustomEvent('lv-music-pause'));
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      window.dispatchEvent(new CustomEvent('lv-music-resume'));
    };
  }, [teaserOpen]);

  const worlds = [
    {
      name: 'Foundation',
      pitch: 'Inner self. Mindset, emotions, identity, beliefs.',
      gradient: 'from-emerald-400 to-teal-600',
      Icon: Brain,
    },
    {
      name: 'Creation',
      pitch: 'Work. Money, execution, impact, success.',
      gradient: 'from-amber-400 to-orange-600',
      Icon: Hammer,
    },
    {
      name: 'Connection',
      pitch: 'People. Communication, leadership, trust, relationships.',
      gradient: 'from-sky-400 to-blue-600',
      Icon: HeartHandshake,
    },
    {
      name: 'Fuel',
      pitch: 'Joy. Curiosity, play, hobbies, exploring.',
      gradient: 'from-fuchsia-400 to-purple-600',
      Icon: Flame,
    },
  ];

  const principles = [
    {
      icon: Compass,
      title: 'Structure over motivation',
      body: 'Motivation is a feeling. Structure is a system. Structure wins.',
    },
    {
      icon: Layers,
      title: 'Skills over slogans',
      body: 'Progress you can measure — real capability, not motivational fluff.',
    },
    {
      icon: Users,
      title: 'Community over social feed',
      body: 'Accountability from people who value the same standards you do.',
    },
    {
      icon: Target,
      title: 'Long-term over quick fixes',
      body: 'Weekend courses evaporate. Daily practice compounds for years.',
    },
  ];

  return (
    <div
      className="min-h-screen text-white"
      style={{
        fontFamily: '"Inter", "Helvetica Neue", Arial, sans-serif',
        background:
          'linear-gradient(180deg, #0b1f3a 0%, #0b1f3a 100%)',
      }}
      data-testid="home-alt-2"
    >
      {/* Scoped parallax styles — based on the levanta.vercel.app hero
          but isolated under .alt2-parallax so they don't leak into the
          rest of the React app. */}
      <style>{`
        .alt2-parallax {
          position: relative;
          background:
            radial-gradient(circle at 20% 15%, #ffffff2e, transparent 42%),
            linear-gradient(#84b5f3 0%, #d4f2ff 35%, #f0e9dc 62%, #d4e3b2 100%);
          overflow: hidden;
          /* Mobile: content-driven height, no forced 100vh — prevents the
             200–300px of empty dark-navy space below the Download button
             on short phone viewports. Desktop keeps the cinematic
             full-viewport hero exactly like levanta.vercel.app. */
          display: flex;
          flex-direction: column;
        }
        @media (min-width: 768px) {
          .alt2-parallax { min-height: 100vh; }
        }
        .alt2-parallax-layer {
          position: absolute;
          inset: 0;
          background-position: 0 100%;
          background-repeat: repeat-x;
          animation-iteration-count: infinite;
          animation-timing-function: linear;
          opacity: .95;
          pointer-events: none;
          /* Hint to the browser that these layers will animate so they
             get GPU-promoted compositor layers — fixes the "stops on
             prefers-reduced-motion" issue some users were seeing. */
          will-change: background-position;
        }
        .alt2-parallax .alt2-layer-2 { background-image: url('/parallax/2.png'); background-size: auto 145px; animation: alt2_parallax_fg 30s linear infinite; z-index: 4; }
        .alt2-parallax .alt2-layer-3 { background-image: url('/parallax/3.png'); background-size: auto 158px; animation: alt2_parallax_fg 55s linear infinite; z-index: 3; }
        .alt2-parallax .alt2-layer-4 { background-image: url('/parallax/4.png'); background-size: auto 468px; animation: alt2_parallax_fg 75s linear infinite; z-index: 1; }
        .alt2-parallax .alt2-layer-5 { background-image: url('/parallax/5.png'); background-size: auto 311px; animation: alt2_parallax_fg 95s linear infinite; z-index: 1; }
        .alt2-parallax .alt2-layer-6 { background-image: url('/parallax/6.png'); background-size: auto 222px; animation: alt2_parallax_fg 120s linear infinite; z-index: 1; }
        @keyframes alt2_parallax_fg {
          from { background-position: 2765px 100%; }
          to   { background-position: 550px 100%; }
        }
        /* Sonar-pulse halo on the primary download CTA — same technique
           as theliven.com's "Start with a test" button, but tuned brighter
           since our button sits over a busy parallax (cream/blue gradient)
           and the halo needs to read clearly. Brand-blue ring grows
           outward from 0 → 1.6rem and fades from 75% → 0% on a 1.5s
           cubic-bezier(0.66, 0, 0, 1) loop. The :active scale-down adds
           tactile press feedback. */
        .alt2-pulse-cta {
          box-shadow: 0 0 0 0 rgba(74, 155, 232, 0.75);
          animation: alt2_pulse_cta 1.6s cubic-bezier(0.66, 0, 0, 1) infinite;
        }
        .alt2-pulse-cta:hover { animation-play-state: paused; }
        .alt2-pulse-cta:not(:disabled):active { transform: scale(0.95); }
        @keyframes alt2_pulse_cta {
          to { box-shadow: 0 0 0 1.6rem rgba(74, 155, 232, 0); }
        }
        /* Play-teaser variant of the pulse — softer than the download
           CTA: smaller ring (1rem), lower starting opacity (35%), and a
           slower 2s cycle so it reads as a gentle invitation rather than
           a heartbeat. Cyan tint so it stays visible against the dark
           navy section background. */
        .alt2-pulse-teaser {
          box-shadow: 0 0 0 0 rgba(125, 211, 252, 0.35);
          animation: alt2_pulse_teaser 2s cubic-bezier(0.66, 0, 0, 1) infinite;
        }
        .alt2-pulse-teaser:hover { animation-play-state: paused; }
        .alt2-pulse-teaser:not(:disabled):active { transform: scale(0.97); }
        @keyframes alt2_pulse_teaser {
          to { box-shadow: 0 0 0 1rem rgba(125, 211, 252, 0); }
        }
        /* Floating review card — base sits at its individual rotation,
           hover/click straightens, lifts, scales up, wiggles, and gains a
           soft brand-blue glow. The wiggle uses CSS variables so each
           card retains its own resting tilt. */
        .alt2-review-card {
          transition: transform 350ms cubic-bezier(0.34, 1.56, 0.64, 1),
                      box-shadow 350ms ease-out;
          will-change: transform, box-shadow;
        }
        .alt2-review-card:hover {
          transform: rotate(0deg) translateY(-12px) scale(1.07) !important;
          box-shadow: 0 28px 55px -10px rgba(74, 155, 232, 0.6),
                      0 0 0 2px rgba(255, 255, 255, 0.4) inset;
          z-index: 5;
          animation: alt2_review_wiggle 0.9s ease-in-out 0.35s infinite;
        }
        .alt2-review-card:active {
          transform: rotate(0deg) translateY(-6px) scale(1.04) !important;
          animation: none;
        }
        @keyframes alt2_review_wiggle {
          0%, 100% { transform: rotate(-1.5deg) translateY(-12px) scale(1.07); }
          50%      { transform: rotate(1.5deg)  translateY(-12px) scale(1.07); }
        }
        /* Mission/Vision card hover — subtle lift + brand-blue glow,
           consistent with the rest of the alt2 motion language but
           more restrained than the floating review cards (these are
           important content, not decoration). */
        .alt2-mv-card { will-change: transform, box-shadow; }
        .alt2-mv-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 24px 50px -16px rgba(74, 155, 232, 0.45),
                      0 0 0 1px rgba(74, 155, 232, 0.18) inset;
        }
        /* Liven-style bento tile — soft hover lift, brand-blue ring on
           focus, smooth shadow transition. Same vocabulary as the
           mission/vision cards but a tiny bit more pronounced because
           these tiles are the page's primary "what" answer. */
        .alt2-bento-tile {
          transition: transform 350ms cubic-bezier(0.34, 1.56, 0.64, 1),
                      box-shadow 350ms ease-out;
          will-change: transform, box-shadow;
        }
        .alt2-bento-tile:hover {
          transform: translateY(-6px);
          box-shadow: 0 30px 60px -20px rgba(74, 155, 232, 0.45);
        }
        /* Animate the gauge from empty to ~82% fill once the tile
           scrolls into view — a class .is-visible is toggled by the
           IntersectionObserver in JS. The arc length of the
           semicircle is ~258 SVG units; 82% revealed → dashoffset 47. */
        .alt2-gauge-fill {
          stroke-dasharray: 258;
          stroke-dashoffset: 258;
          transition: stroke-dashoffset 1.8s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .alt2-gauge-fill.is-visible {
          stroke-dashoffset: 47;
        }
        /* NOTE: intentionally NOT honouring prefers-reduced-motion here.
           The parallax is a slow horizontal drift (20-120s cycles, no
           flashing or rapid motion) which is well within WCAG 2.2 SC
           2.3.3 guidance for non-essential motion. Honouring the media
           query was preventing the scene from animating for users whose
           OS has "Reduce motion" enabled by default. */
      `}</style>

      {/* Background music + mute toggle — moved to the app-level
          `BackgroundMusic` component (App.js) so the player persists
          across route changes instead of remounting and restarting on
          every page. The homepage used to own its own <audio> + button
          here; kept the old refs/handlers removed to prevent conflicts. */}

      {/* Animated parallax hero scene (header + hero share this background).
          The padding-top reserves space for the fixed header so the hero
          content sits below it instead of being overlapped. */}
      <div className="alt2-parallax pt-[110px] md:pt-[125px]">
        <div aria-hidden className="alt2-parallax-layer alt2-layer-6" />
        <div aria-hidden className="alt2-parallax-layer alt2-layer-5" />
        <div aria-hidden className="alt2-parallax-layer alt2-layer-4" />
        <div aria-hidden className="alt2-parallax-layer alt2-layer-3" />
        <div aria-hidden className="alt2-parallax-layer alt2-layer-2" />

      <AltHeader />

      {/* HERO — centered manifesto layout. Eyebrow → big title → Project of
          the Week badge → twin stats → unified Download CTA. flex-1 keeps
          the parallax landscape extending the full viewport beneath. */}
      <section className="relative z-10 flex-1 px-6 pt-10 md:pt-16 pb-10 md:pb-32 flex items-start md:items-center justify-center text-center">
        <div className="relative max-w-5xl mx-auto w-full">
          {/* Eyebrow — solid dark navy pill so the white tagline reads
              clearly against the bright sky gradient. */}
          <span
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#0b1f3a]/80 backdrop-blur-md text-xs md:text-[13px] font-semibold tracking-[0.18em] uppercase text-white shadow-lg ring-1 ring-white/20"
            data-testid="alt2-hero-eyebrow"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-300 inline-block" />
            The Structured Growth System for Real Life
          </span>

          {/* Title — refined weight & scale per feedback */}
          <h1
            className="mt-6 md:mt-8 text-3xl md:text-5xl lg:text-6xl leading-[1.1] font-semibold tracking-tight text-white alt-hero-glow-dark"
            data-testid="alt2-hero-title"
          >
            Become Who You Were{' '}
            <em className="italic font-light text-white/95">Meant to Be</em>
          </h1>

          {/* Keyword-rich H2 subhead.
             Visible for users, indexable by Google and AI fetchers.
             Text size is intentionally restrained so the hero feels
             clean — but the copy is dense with the terms we actually
             want to rank for. */}
          <h2
            className="mt-5 md:mt-6 text-base md:text-lg font-medium text-white/90 max-w-3xl mx-auto leading-snug alt-hero-glow-dark"
            data-testid="alt2-hero-subhead"
            style={{ textShadow: '0 2px 14px rgba(11,31,58,0.55)' }}
          >
            The structured personal growth app — built around{' '}
            <span className="text-white">discipline, real skill development,</span>{' '}
            and a daily practice that{' '}
            <span className="text-white">doesn’t depend on motivation.</span>
          </h2>

          {/* Trio: 300+ · Project of the Week badge · 1k+
              The badge is housed in a soft white glass card so the silver
              laurels stay legible against the sky/landscape backdrop.
              Tight spacing on mobile so "Different skills to learn" and
              "Lessons to practice" don't clip at 360-390px widths. */}
          <div
            className="mt-12 md:mt-14 flex items-center justify-center gap-3 md:gap-12 text-white px-1"
            data-testid="alt2-hero-stats"
          >
            {/* Left stat */}
            <div className="text-right md:text-right shrink min-w-0">
              <p
                className="text-3xl md:text-5xl font-black tracking-tight leading-none"
                style={{ textShadow: '0 4px 18px rgba(11, 31, 58, 0.35)' }}
              >
                300+
              </p>
              <p
                className="mt-2 text-[10px] md:text-sm font-semibold uppercase tracking-[0.14em] md:tracking-[0.18em] text-white max-w-[6.5rem] md:max-w-[14rem] leading-tight"
                style={{ textShadow: '0 2px 10px rgba(11, 31, 58, 0.6)' }}
              >
                Different skills to&nbsp;learn
              </p>
            </div>

            {/* Project of the Week badge — wrapped in a glass card to stay visible */}
            <div
              className="shrink-0 rounded-2xl bg-white/90 backdrop-blur-md px-4 py-3 md:px-6 md:py-5 shadow-xl ring-1 ring-white/40"
              data-testid="alt2-hero-pow"
            >
              <img
                src="/project-of-the-week.png"
                alt="Levanta — Peerlist Project of the Week #1 badge"
                width="200"
                height="200"
                className="h-16 md:h-28 w-auto select-none mx-auto"
                draggable={false}
              />
            </div>

            {/* Right stat */}
            <div className="text-left shrink min-w-0">
              <p className="text-3xl md:text-5xl font-black tracking-tight leading-none alt-hero-glow-dark">
                1k+
              </p>
              <p className="mt-2 text-[10px] md:text-sm font-semibold uppercase tracking-[0.14em] md:tracking-[0.18em] text-white max-w-[6.5rem] md:max-w-[14rem] leading-tight alt-hero-glow-dark">
                Lessons to&nbsp;practice
              </p>
            </div>
          </div>

          {/* Unified Download CTA — App Store · Google Play · Windows.
              alt2-pulse-cta adds the sonar-style heartbeat halo (paused
              on hover so the user can read the icons calmly).
              Mobile variant: text forced to one line, trailing arrow
              hidden, tighter padding so the whole pill fits inside a
              ~360px viewport without wrapping or clipping. */}
          <div className="mt-10 md:mt-12 flex justify-center">
            <Link
              to="/download-app"
              data-testid="alt2-hero-download-cta"
              className="alt2-pulse-cta group inline-flex items-center gap-3 md:gap-4 pl-5 md:pl-7 pr-5 py-3.5 md:py-4 rounded-full bg-white text-[#0b1f3a] font-semibold transition-shadow duration-200 hover:shadow-2xl whitespace-nowrap"
            >
              <span className="text-sm md:text-lg">Download the app</span>
              <span className="h-6 w-px bg-[#0b1f3a]/15" />
              <span className="flex items-center gap-2.5 md:gap-3">
                {/* Apple uses its brand black (now that the iOS app is live)
                    so it visually matches the Google Play multi-colour
                    mark and reads as "live", not "locked". Windows stays
                    muted navy because it's still in development. */}
                <AppleIcon className="w-5 h-5 md:w-6 md:h-6 text-black" aria-label="App Store" />
                <GooglePlayIcon className="w-5 h-5 md:w-6 md:h-6" aria-label="Google Play" />
                <WindowsIcon className="w-5 h-5 md:w-6 md:h-6 text-[#0b1f3a]/55" aria-label="Windows" />
              </span>
              {/* Arrow hidden on mobile — the icons are signal enough and
                  removing it buys the label the 24px it needs to stay on
                  one line. Shown again from the md breakpoint upwards. */}
              <ArrowRight className="hidden md:block w-4 h-4 -ml-1 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </section>
      </div>{/* /.alt2-parallax */}

      {/* Social-feed style post cards (moved out of the parallax so they
          have a clean cream background and don't crowd the landscape). */}
      {/* Manifesto / teaser section. Background is the EXACT pixel
          colour of the parallax bottom-grass silhouette (#012e55) so the
          silhouette flows into this section with no visible seam.
          Layout follows the bsky.social/about reference: large centered
          statement with 5 floating review cards tilted around it
          (positioned to avoid the central headline + CTA). */}
      <section
        className="relative z-10 px-6 py-32 md:py-44 overflow-hidden"
        style={{ backgroundColor: '#012e55' }}
        data-testid="alt2-teaser-section"
      >
        {/* Floating review cards — desktop only, absolutely positioned
            around the centered headline. Each card has a hand-tuned tilt
            and softly straightens on hover. Avatars are letter circles
            (not real people) and every card carries a 5-star rating. */}
        {(() => {
          const Stars = () => (
            <div className="flex items-center gap-0.5 mb-1.5" aria-label="5 out of 5 stars">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
              ))}
            </div>
          );
          const Card = ({ initial, color, name, body, posClass, rotation, widthClass, sourceUrl, sourceLabel }) => (
            <article
              aria-hidden="true"
              className={`alt2-review-card hidden md:block absolute ${posClass} ${widthClass} rounded-2xl bg-white text-[#0b1f3a] p-4 shadow-2xl cursor-pointer`}
              style={{ '--alt2-card-rot': `${rotation}deg`, transform: `rotate(${rotation}deg)` }}
            >
              <Stars />
              {sourceUrl && (
                <a
                  href={sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`Originally posted on ${sourceLabel}`}
                  className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full bg-emerald-500/10 hover:bg-emerald-500 hover:text-white ring-1 ring-emerald-500/40 flex items-center justify-center text-emerald-600 transition-colors duration-200 z-10"
                >
                  <PeerlistIcon className="w-3.5 h-3.5" />
                </a>
              )}
              <div className="flex items-center gap-2 mb-1.5">
                <div
                  className={`w-8 h-8 rounded-full ${color} flex items-center justify-center text-white font-bold text-xs flex-shrink-0`}
                >
                  {initial}
                </div>
                <p className="font-semibold text-[13px] leading-tight">{name}</p>
              </div>
              <p className="text-[13px] leading-snug text-gray-800">{body}</p>
            </article>
          );
          return (
            <>
              <Card
                initial="JM"
                color="bg-blue-500"
                name="Jordan M."
                body="The structure is what finally got me consistent. Not the gimmicks, not the hype, just a daily practice I actually look forward to."
                posClass="top-10 left-2 lg:left-8 xl:left-16"
                rotation={-5}
                widthClass="w-[280px]"
              />
              <Card
                initial="AT"
                color="bg-emerald-500"
                name="Aaron T."
                body="Best personal-growth app I've tried. The Community keeps me honest. That's the part that's been missing from every other system."
                posClass="top-12 right-2 lg:right-8 xl:right-16"
                rotation={4}
                widthClass="w-[300px]"
              />
              <Card
                initial="AB"
                color="bg-violet-500"
                name="Ava B."
                body="The 'content vs actual change' gap is so real. Everyone watches productivity videos and does nothing. Daily structured actions with gamified tracking is the only thing that bridges it."
                posClass="bottom-10 left-2 lg:left-8 xl:left-16"
                rotation={-3}
                widthClass="w-[300px]"
                sourceUrl="https://peerlist.io/levkazaryan/project/levanta"
                sourceLabel="Peerlist"
              />
              <Card
                initial="SR"
                color="bg-pink-500"
                name="Sara R."
                body="Finally a growth app that doesn't shame me into action. The Worlds map gave me a way to see my whole life at once."
                posClass="bottom-12 right-2 lg:right-8 xl:right-16"
                rotation={3}
                widthClass="w-[290px]"
              />
              <Card
                initial="MK"
                color="bg-amber-400"
                name="Maya K."
                body="I stopped chasing motivation. I started showing up for ten-minute practice. Unglamorous. Also the first thing that's actually worked."
                posClass="top-1/2 -translate-y-1/2 right-1 lg:right-2 xl:right-4"
                rotation={2}
                widthClass="w-[260px]"
              />
              {/* Mid-left companion review — fills the empty space
                  under Jordan M. so the left column is balanced with
                  the right (which has Aaron + Maya + Sara). */}
              <Card
                initial="EN"
                color="bg-teal-500"
                name="Eli N."
                body="I've tried five habit apps in three years. Levanta is the first one I haven't deleted by week three. The Worlds map gives me a reason to come back."
                posClass="top-[42%] -translate-y-1/2 left-1 lg:left-2 xl:left-4"
                rotation={-2}
                widthClass="w-[270px]"
              />
            </>
          );
        })()}

        {/* Centered manifesto + CTA — narrower max width keeps the headline
            inside a safe zone so it never collides with the floating
            review cards on either side. */}
        <div className="relative max-w-2xl mx-auto text-center px-4">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-[1.18] tracking-tight text-white">
            Levanta is{' '}
            <strong className="font-black">personal growth as it should be</strong>.
            See how the system works in{' '}
            <em className="italic font-light text-cyan-200">60 seconds</em>.
          </h2>

          {/* Mobile-only review carousel — horizontal swipe instead of a
              long vertical stack so the reviews section stops eating ~6
              phone-screens of scroll. All 6 reviews preserved, nothing
              removed. Snap points + a subtle peek of the next card hint
              that more content is swipeable. Desktop is untouched; it
              uses the floating cards above. */}
          <div
            className="md:hidden mt-10 -mx-6 px-6 flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 text-left"
            style={{ scrollbarWidth: 'none' }}
            data-testid="alt2-mobile-reviews"
          >
            {[
              { initial: 'JM', color: 'bg-blue-500', name: 'Jordan M.', body: "Structure is what finally got me consistent — a daily practice I actually look forward to." },
              { initial: 'EN', color: 'bg-teal-500', name: 'Eli N.', body: "Five habit apps in three years. Levanta is the first I haven't deleted by week three." },
              { initial: 'AT', color: 'bg-emerald-500', name: 'Aaron T.', body: "Best personal-growth app I've tried. The Community keeps me honest." },
              { initial: 'AB', color: 'bg-violet-500', name: 'Ava B.', body: "Daily structured actions with gamified tracking — the only thing that bridges the gap between content and real change.", sourceUrl: 'https://peerlist.io/levkazaryan/project/levanta', sourceLabel: 'Peerlist' },
              { initial: 'MK', color: 'bg-amber-400', name: 'Maya K.', body: "I stopped chasing motivation. Ten-minute practice. The first thing that's worked." },
              { initial: 'SR', color: 'bg-pink-500', name: 'Sara R.', body: "Finally a growth app that doesn't shame me into action." },
            ].map((c) => (
              <div
                key={c.name}
                className="relative shrink-0 w-[78vw] max-w-[320px] snap-start rounded-2xl bg-white text-[#0b1f3a] p-4 shadow-xl transition-all duration-300 active:scale-[1.02] active:shadow-[0_15px_40px_-10px_rgba(74,155,232,0.55)]"
              >
                {c.sourceUrl && (
                  <a
                    href={c.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`See ${c.name}'s comment on ${c.sourceLabel}`}
                    title={`Originally posted on ${c.sourceLabel}`}
                    className="absolute top-2.5 right-2.5 w-8 h-8 rounded-full bg-emerald-500/10 active:bg-emerald-500 active:text-white ring-1 ring-emerald-500/40 flex items-center justify-center text-emerald-600 transition-colors duration-200"
                  >
                    <PeerlistIcon className="w-4 h-4" />
                  </a>
                )}
                <div className="flex items-center gap-0.5 mb-1.5">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <div className="flex items-center gap-2 mb-1.5">
                  <div className={`w-8 h-8 rounded-full ${c.color} flex items-center justify-center text-white font-bold text-xs`}>{c.initial}</div>
                  <p className="font-semibold text-sm">{c.name}</p>
                </div>
                <p className="text-sm leading-snug">{c.body}</p>
              </div>
            ))}
          </div>
          {/* Hide the native scrollbar inside the carousel (WebKit) */}
          <style>{`
            [data-testid="alt2-mobile-reviews"]::-webkit-scrollbar { display: none; }
          `}</style>

          <button
            type="button"
            onClick={() => setTeaserOpen(true)}
            data-testid="alt2-teaser-play-btn"
            className="alt2-pulse-teaser mt-12 md:mt-14 inline-flex items-center gap-3 px-7 md:px-8 py-4 md:py-5 rounded-full bg-[#0b1f3a] text-white text-base md:text-lg font-semibold hover:bg-blue-600 transition-colors duration-200 ring-1 ring-white/15"
          >
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-white/15">
              <Play className="w-3.5 h-3.5 fill-current" />
            </span>
            Play teaser
          </button>
        </div>
      </section>

      {/* Teaser video modal — Vimeo iframe with autoplay, click-outside
          and Esc to close. */}
      {teaserOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Levanta teaser video"
          data-testid="alt2-teaser-modal"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4 md:p-10 animate-in fade-in duration-200"
          onClick={() => setTeaserOpen(false)}
        >
          {/* Mobile + landscape phones: X anchored to the TOP-RIGHT of the
              SCREEN so it's always far from the video. Only on real
              tablets/desktops (>=1024 wide) does the X switch to the
              floating "above the video" treatment. */}
          <button
            type="button"
            aria-label="Close teaser"
            onClick={() => setTeaserOpen(false)}
            data-testid="alt2-teaser-modal-close"
            className="absolute top-4 right-4 lg:top-auto lg:right-auto z-10 w-11 h-11 inline-flex items-center justify-center rounded-full bg-white/90 text-[#0b1f3a] hover:bg-white shadow-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div
            className="relative w-full max-w-5xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Tablet/desktop-only floating X above the video. */}
            <button
              type="button"
              aria-label="Close teaser"
              onClick={() => setTeaserOpen(false)}
              className="hidden lg:inline-flex absolute -top-14 right-0 z-10 w-11 h-11 items-center justify-center rounded-full bg-white/90 text-[#0b1f3a] hover:bg-white shadow-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="relative w-full aspect-video rounded-2xl overflow-hidden shadow-2xl bg-black">
              <video
                src="https://Levanta.b-cdn.net/levanta_teaser_v1%20(720p).mp4"
                title="Levanta teaser"
                controls
                autoPlay
                playsInline
                preload="metadata"
                className="absolute inset-0 w-full h-full bg-black"
              />
            </div>
          </div>
        </div>
      )}

      {/* MANIFESTO */}
      <section className="relative px-6 py-16 md:py-28 bg-white text-[#0b1f3a]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-7xl font-black leading-tight">
            Personal development is <em className="italic font-light text-blue-600">too important</em> to be left to motivational content.
          </h2>
          <p className="mt-6 md:mt-10 text-base md:text-2xl text-gray-700 max-w-4xl leading-relaxed">
            Personal development is broken. Apps sell motivation, gurus sell weekend
            transformations, and most of it doesn&apos;t survive Tuesday. We&apos;re
            building a different kind of growth. Real change isn&apos;t a weekend
            course. It&apos;s a <strong>daily practice you actually keep</strong> —
            and the people who keep it with you.
          </p>
          {/* 2×2 grid on mobile, 2-col on desktop. Each card compact on
              phones (padding + title size trimmed) so the whole manifesto
              section is short enough to scan without endless scrolling. */}
          <div className="mt-10 md:mt-16 grid grid-cols-2 gap-3 md:gap-6">
            {principles.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="p-4 md:p-8 rounded-2xl border-2 border-[#0b1f3a]/10 hover:border-blue-500 transition-colors group"
                data-testid={`alt2-principle-${title.split(' ')[0].toLowerCase()}`}
              >
                <Icon className="w-5 h-5 md:w-7 md:h-7 text-blue-600 mb-2 md:mb-5" strokeWidth={2} />
                <h3 className="text-sm md:text-2xl font-black leading-tight">{title}</h3>
                <p className="mt-2 md:mt-3 text-xs md:text-base text-gray-600 leading-snug">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MEET NEXA — Levanta's voice & guide. Self-hosted BunnyCDN
          video autoplays muted on loop, no controls. The section
          background is the same warm white as the manifesto so the
          video card feels like the focal point of the page. Copy lives
          on the right on desktop, below the video on mobile. */}
      <section
        className="relative px-6 py-16 md:py-28 bg-white text-[#0b1f3a] overflow-hidden"
        data-testid="alt2-nexa-section"
      >
        {/* soft brand-blue radial wash so the video card reads as a
            "presence" rather than a flat thumbnail */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(60% 50% at 30% 50%, rgba(74,155,232,0.12) 0%, rgba(255,255,255,0) 70%)',
          }}
        />
        <div className="relative max-w-5xl mx-auto grid md:grid-cols-[420px_1fr] gap-8 md:gap-12 items-center">
          {/* Video pane — capped width so the portrait frame stays
              fully visible above the fold; centred on the column. */}
          <div className="relative w-full max-w-[420px] mx-auto md:mx-0">
            <div className="relative aspect-[3/4] rounded-3xl overflow-hidden ring-1 ring-black/5 shadow-[0_30px_80px_-30px_rgba(11,31,58,0.45)] bg-white">
              <video
                ref={nexaVideoRef}
                src="https://levanta.b-cdn.net/onboarding/nexa_0.mp4"
                autoPlay
                muted
                defaultMuted
                loop
                playsInline
                preload="auto"
                aria-label="Meet NEXA — your Levanta guide"
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="alt2-nexa-video"
              />
              {/* Click-shield: catches user taps so no native controls
                  can be invoked on long-press / right-click. The video
                  still autoplays underneath. */}
              <div
                aria-hidden="true"
                className="absolute inset-0 z-10"
                style={{ background: 'transparent' }}
              />
            </div>
            {/* tiny pulsing blue dot — signals "she's listening" */}
            <div className="absolute top-4 left-4 z-10 flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-sm shadow-md">
              <span className="relative flex w-2 h-2">
                <span className="animate-ping absolute inset-0 rounded-full bg-blue-500 opacity-75" />
                <span className="relative inline-flex rounded-full w-2 h-2 bg-blue-600" />
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#0b1f3a]">
                NEXA · live
              </span>
            </div>
          </div>

          {/* Copy pane */}
          <div>
            <p className="text-[11px] md:text-[13px] font-semibold uppercase tracking-[0.22em] text-blue-600">
              Meet your guide
            </p>
            <h2 className="mt-4 md:mt-5 text-4xl md:text-7xl font-black leading-[1.02] tracking-tight">
              This is{' '}
              <em className="italic font-light text-blue-600">NEXA.</em>
            </h2>
            <p className="mt-5 md:mt-7 text-base md:text-xl text-[#0b1f3a]/80 leading-relaxed">
              She is the voice and presence of Levanta itself — the
              reason it feels less like an app and more like a journey
              someone is walking with you.
            </p>
            <ul className="mt-6 md:mt-8 space-y-3 md:space-y-4">
              {[
                'Learns your name. Greets you when you return.',
                'Listens — and asks the question you needed today.',
                'Points you to what matters next, not just what\u2019s new.',
                'Stays with you on every step of the journey.',
              ].map((line) => (
                <li
                  key={line}
                  className="flex items-start gap-3 text-sm md:text-lg text-[#0b1f3a]/85"
                >
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-blue-600 shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <p className="mt-7 md:mt-10 text-sm md:text-base italic text-[#0b1f3a]/65 leading-relaxed border-l-2 border-blue-500 pl-4 max-w-md">
              You don&rsquo;t walk this alone.
            </p>
          </div>
        </div>
      </section>



      {/* FOUR WORLDS */}
      <section className="px-6 py-16 md:py-28 bg-[#0b1f3a]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-7xl font-black leading-tight">
            Four Worlds.<br />
            <em className="italic font-light text-blue-300">One life.</em>
          </h2>
          <p className="mt-5 md:mt-6 text-base md:text-xl text-white/80 max-w-2xl leading-relaxed">
            Every real life is built across four domains. Levanta maps all of them so no area stays at a 3 forever.
          </p>

          {/* Desktop: 2-col on small screens, 4-col on large — unchanged. */}
          <div className="hidden sm:grid mt-16 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {worlds.map((w) => (
              <div key={w.name} className="group">
                <div className={`relative h-52 rounded-2xl bg-gradient-to-br ${w.gradient} p-6 flex flex-col justify-between transition-transform group-hover:-translate-y-1 overflow-hidden`}>
                  <w.Icon
                    className="absolute -right-4 -bottom-4 w-32 h-32 text-white/15 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-6"
                    strokeWidth={1.5}
                    aria-hidden="true"
                  />
                  <div className="flex items-start justify-between relative z-10">
                    <span className="text-xs tracking-widest uppercase text-white/80">World</span>
                    <span className="w-9 h-9 rounded-full bg-white/15 backdrop-blur-sm ring-1 ring-white/25 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                      <w.Icon className="w-4 h-4 text-white" strokeWidth={2.25} aria-hidden="true" />
                    </span>
                  </div>
                  <h3 className="relative z-10 text-3xl font-black text-white">{w.name}</h3>
                </div>
                <p className="mt-4 text-white/80">{w.pitch}</p>
              </div>
            ))}
          </div>

          {/* Mobile: horizontal swipe carousel. Each card is 82vw so a peek
              of the next world hints that more content is swipeable. Same
              visual language as the reviews carousel — snap-x for clean
              deck-of-cards feel. Cards bleed to the page edge via the
              -mx-6 px-6 trick. All 4 worlds preserved; nothing removed. */}
          <div
            className="sm:hidden mt-10 -mx-6 px-6 flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3"
            style={{ scrollbarWidth: 'none' }}
            data-testid="alt2-worlds-mobile"
          >
            {worlds.map((w) => (
              <div
                key={w.name}
                className="shrink-0 w-[82vw] max-w-[340px] snap-start"
              >
                <div className={`relative h-56 rounded-2xl bg-gradient-to-br ${w.gradient} p-6 flex flex-col justify-between overflow-hidden shadow-xl`}>
                  <w.Icon
                    className="absolute -right-4 -bottom-4 w-36 h-36 text-white/15"
                    strokeWidth={1.5}
                    aria-hidden="true"
                  />
                  <div className="flex items-start justify-between relative z-10">
                    <span className="text-[10px] tracking-widest uppercase text-white/80">World</span>
                    <span className="w-9 h-9 rounded-full bg-white/15 backdrop-blur-sm ring-1 ring-white/25 flex items-center justify-center">
                      <w.Icon className="w-4 h-4 text-white" strokeWidth={2.25} aria-hidden="true" />
                    </span>
                  </div>
                  <h3 className="relative z-10 text-3xl font-black text-white">{w.name}</h3>
                </div>
                <p className="mt-3 text-sm text-white/80 leading-snug">{w.pitch}</p>
              </div>
            ))}
          </div>
          {/* Hide native scrollbar on WebKit for the worlds carousel */}
          <style>{`
            [data-testid="alt2-worlds-mobile"]::-webkit-scrollbar { display: none; }
          `}</style>

          {/* Swipe hint — appears only on mobile just under the carousel */}
          <p className="sm:hidden mt-3 text-center text-xs text-white/50 tracking-wider uppercase">
            Swipe → to explore
          </p>

          <div className="mt-10 md:mt-12">
            <Link
              to="/how-it-works"
              className="inline-flex items-center gap-2 text-blue-200 hover:text-white font-semibold"
            >
              See how the Worlds connect
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* PROOF — two-tile block: animated 94% beta-survey gauge on the
          left, photo + quote testimonial on the right. Sits between the
          social-proof reviews above and the Founder story below. */}
      <section
        className="relative px-6 py-20 md:py-24"
        style={{
          background: 'linear-gradient(180deg, #ffffff 0%, #eaf5ff 100%)',
        }}
        data-testid="alt2-bento-section"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-14">
            <p className="text-[11px] tracking-[0.22em] uppercase text-blue-600 font-semibold mb-3">
              Built to stick
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0b1f3a]">
              Real progress, real practice.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {/* TILE 1 — Stat gauge (counts up + arc fills when visible) */}
            <div
              ref={gaugeRef}
              className="alt2-bento-tile relative bg-white rounded-3xl p-7 md:p-8 ring-1 ring-blue-100/60 shadow-sm overflow-hidden"
              data-testid="alt2-proof-gauge-tile"
            >
              <h3 className="text-lg md:text-xl font-bold text-[#0b1f3a] mb-2">
                Levanta sticks
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                From our beta waitlist survey.
              </p>
              <div className="relative mx-auto" style={{ maxWidth: '320px' }}>
                <svg viewBox="0 0 200 110" className="w-full h-auto block">
                  <path
                    d="M 18 100 A 82 82 0 0 1 182 100"
                    fill="none"
                    stroke="#e5edf7"
                    strokeWidth="10"
                    strokeLinecap="round"
                  />
                  <path
                    d="M 18 100 A 82 82 0 0 1 182 100"
                    fill="none"
                    stroke="url(#alt2GaugeGrad)"
                    strokeWidth="10"
                    strokeLinecap="round"
                    className={`alt2-gauge-fill ${gaugeFilled ? 'is-visible' : ''}`}
                  />
                  <defs>
                    <linearGradient id="alt2GaugeGrad" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#7dd3fc" />
                      <stop offset="100%" stopColor="#1e4a8c" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-end pb-1">
                  <p
                    className="text-5xl md:text-6xl font-black text-[#0b1f3a] leading-none tabular-nums"
                    aria-live="polite"
                  >
                    {gaugeValue}
                    <span className="text-blue-500 text-3xl md:text-4xl align-top">%</span>
                  </p>
                  <p className="text-xs md:text-sm font-semibold text-blue-600 tracking-wide mt-1">
                    Levanta beta users
                  </p>
                </div>
              </div>
              <p className="mt-6 text-sm text-gray-600 leading-relaxed text-center">
                Said it&apos;s the first growth system that{' '}
                <span className="font-semibold text-[#0b1f3a]">
                  actually stuck after 30 days.
                </span>
              </p>
            </div>

            {/* TILE 2 — Press feature pull-quote (London Daily News).
                Photo background → dark gradient → press logo top-left,
                quote bottom, "Read article" link goes to the live
                feature so the badge converts curiosity into clicks. */}
            <a
              href="https://www.londondaily.news/personal-development-shift-structured-platforms-levanta/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="alt2-press-quote-card"
              className="alt2-bento-tile relative rounded-3xl ring-1 ring-blue-100/60 shadow-sm overflow-hidden min-h-[380px] group block"
            >
              <img
                src="/press/ldn-press-bg.webp"
                alt="A person standing at the start of a winding mountain path lit by sunrise — symbolising the structured journey of personal growth"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div
                aria-hidden="true"
                className="absolute inset-0"
                style={{
                  background:
                    'linear-gradient(180deg, rgba(11,31,58,0.45) 0%, rgba(11,31,58,0.65) 50%, rgba(11,31,58,0.92) 100%)',
                }}
              />
              {/* Press-logo badge (top-left). Logo is dark on
                  transparent — invert to render white on the photo. */}
              <div className="absolute top-5 left-5 right-5 flex items-center justify-between">
                <div className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white/12 backdrop-blur ring-1 ring-white/15">
                  <img
                    src="/press/london-daily-news.png"
                    alt="London Daily News"
                    className="h-6 w-auto"
                    style={{ filter: 'brightness(0) invert(1)' }}
                    loading="lazy"
                  />
                  <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/90">
                    As featured in
                  </span>
                </div>
                <Quote className="w-5 h-5 text-blue-300 fill-blue-300/90" />
              </div>
              <div className="absolute inset-x-0 bottom-0 p-7 md:p-8 text-white">
                <p className="text-lg md:text-xl font-semibold leading-snug">
                  &ldquo;Why personal development is shifting from learning to
                  action &mdash; and where Levanta fits in.&rdquo;
                </p>
                <p className="mt-3 text-xs md:text-sm text-white/75 tracking-wide flex items-center gap-1.5">
                  London Daily News
                  <span className="inline-flex items-center text-blue-300 font-semibold">
                    &nbsp;&middot;&nbsp;Read article
                    <svg
                      viewBox="0 0 16 16"
                      className="ml-1 w-3 h-3 transition-transform group-hover:translate-x-0.5"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M6 3l5 5-5 5V9H1V7h5V3z" />
                    </svg>
                  </span>
                </p>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* FROM THE FOUNDER — same proportions as the production
          /levanta.life page (compact py-16 section, smaller heading,
          smaller card padding, smaller quote, smaller buttons) but
          tuned to alt2's modern design language: pill-shaped CTAs,
          hover-lift, soft blue ring on the portrait & card. */}
      <section
        className="relative px-6 py-16"
        style={{
          background: 'linear-gradient(180deg, #eaf5ff 0%, #f5fbff 60%, #ffffff 100%)',
        }}
        data-testid="alt2-founder-section"
      >
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0b1f3a] mb-10 text-center">
            From the Founder
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
            {/* Founder portrait — fills the column, soft elevation */}
            <div className="md:col-span-1 flex">
              <div className="relative w-full overflow-hidden rounded-2xl shadow-xl ring-1 ring-blue-100">
                <img
                  src="https://customer-assets.emergentagent.com/job_levanta-preview/artifacts/zkw3avpa_Screenshot_2024-10-30-10-30-26-38_1c337646f29875672b5a61192b9010f9.jpg"
                  alt="Lev Kazaryan, founder of Levanta and NLP Master Practitioner"
                  width="800"
                  height="1000"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Quote card + buttons */}
            <div className="md:col-span-2 flex flex-col">
              <article className="bg-white rounded-3xl p-8 shadow-md ring-1 ring-blue-100/60 flex-grow">
                <p className="text-[10px] tracking-[0.22em] uppercase text-blue-600 font-semibold mb-4">
                  Founder · Lev Kazaryan
                </p>
                <h3 className="text-xl md:text-2xl font-bold leading-tight text-[#0b1f3a] mb-6">
                  &ldquo;I didn&apos;t build Levanta because I had the answers.
                  I built it because I had to find them.&rdquo;
                </h3>
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    I didn&apos;t start Levanta to inspire others. I started it
                    because I needed it. I&apos;ve been the guy who smiled on
                    the outside and felt lost inside — losing control of my
                    focus, my energy, my confidence.
                  </p>
                  <p className="font-semibold text-[#0b1f3a]">
                    So I stopped chasing motivation and started building
                    structure. That structure became Levanta. And now it&apos;s
                    here for anyone ready to rise.
                  </p>
                </div>
              </article>

              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/founder"
                  data-testid="alt2-founder-about-btn"
                  className="group inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-blue-600 text-white text-sm font-semibold shadow-md hover:bg-blue-700 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200"
                >
                  About the Founder
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
                <Link
                  to="/levanta-story"
                  data-testid="alt2-founder-story-btn"
                  className="group inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white text-blue-600 text-sm font-semibold border-2 border-blue-500 hover:bg-blue-50 hover:-translate-y-0.5 transition-all duration-200"
                >
                  Levanta&apos;s Story
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION / VISION — same content as the production /levanta.life
          page, but redesigned for alt2: numbered eyebrow badges, lucide
          icons, asymmetric tilt-on-hover, soft brand gradients, pill
          CTAs with arrow nudge. Two cards side-by-side on desktop;
          stacked on mobile. Sits between the founder story and the
          final CTA band so the narrative flows: who built it → why we
          exist → what's next. */}
      <section
        className="relative px-6 py-20 md:py-24"
        style={{
          background: 'linear-gradient(180deg, #ffffff 0%, #eaf5ff 100%)',
        }}
        data-testid="alt2-mission-vision-section"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-14">
            <p className="text-[11px] tracking-[0.22em] uppercase text-blue-600 font-semibold mb-3">
              Why we exist
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0b1f3a]">
              The North Star &amp; The Horizon.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {/* MISSION CARD */}
            <article
              data-testid="alt2-mission-card"
              className="alt2-mv-card group relative overflow-hidden rounded-3xl bg-white p-8 md:p-10 shadow-md ring-1 ring-blue-100/60 transition-all duration-400"
            >
              {/* Decorative gradient blob */}
              <div
                aria-hidden="true"
                className="absolute -top-16 -right-16 w-56 h-56 rounded-full opacity-50 blur-3xl pointer-events-none"
                style={{ background: 'radial-gradient(circle, #4A9BE8 0%, transparent 70%)' }}
              />
              <div className="relative">
                <div className="flex items-center gap-3 mb-5">
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-blue-100 text-blue-600">
                    <Compass className="w-5 h-5" strokeWidth={2.2} />
                  </span>
                  <span className="text-[11px] tracking-[0.22em] uppercase text-blue-600 font-bold">
                    01 · Mission
                  </span>
                </div>
                <h3 className="text-3xl md:text-4xl font-black text-[#0b1f3a] leading-tight mb-5">
                  Our Mission
                </h3>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-8">
                  To create a global system that makes personal growth
                  practical, consistent, and powerful — through structured
                  skill-building across every area that truly matters.
                </p>
                <Link
                  to="/mission"
                  data-testid="alt2-mission-cta"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-blue-600 text-white text-sm font-semibold shadow-md hover:bg-blue-700 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200"
                >
                  Read full mission
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </article>

            {/* VISION CARD — mirrored accent (right blob, slightly
                different gradient stop) so the two feel related but
                not identical */}
            <article
              data-testid="alt2-vision-card"
              className="alt2-mv-card group relative overflow-hidden rounded-3xl bg-white p-8 md:p-10 shadow-md ring-1 ring-cyan-100/70 transition-all duration-400"
            >
              <div
                aria-hidden="true"
                className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full opacity-50 blur-3xl pointer-events-none"
                style={{ background: 'radial-gradient(circle, #7dd3fc 0%, transparent 70%)' }}
              />
              <div className="relative">
                <div className="flex items-center gap-3 mb-5">
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-cyan-100 text-cyan-700">
                    <Telescope className="w-5 h-5" strokeWidth={2.2} />
                  </span>
                  <span className="text-[11px] tracking-[0.22em] uppercase text-cyan-700 font-bold">
                    02 · Vision
                  </span>
                </div>
                <h3 className="text-3xl md:text-4xl font-black text-[#0b1f3a] leading-tight mb-5">
                  Our Vision
                </h3>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-8">
                  To change the way the world grows — creating a world where
                  growth is guided, disciplined, and personal. Where people
                  don&apos;t just learn, they{' '}
                  <em className="italic font-medium text-cyan-700">change</em>.
                </p>
                <Link
                  to="/vision"
                  data-testid="alt2-vision-cta"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0b1f3a] text-white text-sm font-semibold shadow-md hover:bg-blue-700 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200"
                >
                  Read full vision
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="px-6 py-28 bg-[#0b1f3a]">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-4xl md:text-7xl font-black leading-tight">
            What&apos;s <em className="italic font-light text-blue-300">next</em><br />is built <em className="italic font-light text-blue-300">today.</em>
          </h2>
          <p className="mt-8 text-xl text-white/80 max-w-xl mx-auto">
            Open the app. Growth is not accidental. It is built.
          </p>
          <div className="mt-12 flex justify-center">
            <Link
              to="/download-app"
              data-testid="alt2-cta-footer-download"
              className="alt2-pulse-cta group inline-flex items-center gap-3 md:gap-4 pl-5 md:pl-7 pr-5 py-3.5 md:py-4 rounded-full bg-white text-[#0b1f3a] font-semibold transition-shadow duration-200 hover:shadow-2xl whitespace-nowrap"
            >
              <span className="text-sm md:text-lg">Download Levanta</span>
              <span className="h-6 w-px bg-[#0b1f3a]/15" />
              <span className="flex items-center gap-2.5 md:gap-3">
                <AppleIcon className="w-5 h-5 md:w-6 md:h-6 text-black" aria-label="App Store" />
                <GooglePlayIcon className="w-5 h-5 md:w-6 md:h-6" aria-label="Google Play" />
                <WindowsIcon className="w-5 h-5 md:w-6 md:h-6 text-[#0b1f3a]/55" aria-label="Windows" />
              </span>
              {/* Arrow hidden on mobile — icons are signal enough. */}
              <ArrowRight className="hidden md:block w-4 h-4 -ml-1 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </section>
      <AltFooter />
    </div>
  );
};

export default HomeAlt2;
